import React from "react";
import { createClippingJob } from "../api/clippingApi";
import { useBackgroundJobs } from "../../../../context/BackgroundJobsContext";

const INITIAL = {
  phase: "idle",
  requestId: null,
  status: null,
  result: null,
  error: null,
  createMeta: null,
  jobKey: null,
};

function mapBackgroundToState(bg) {
  if (!bg) return INITIAL;
  return {
    phase: bg.phase === "generating" ? "polling" : bg.phase,
    requestId: bg.externalId,
    status: bg.status,
    result: bg.result,
    error: bg.error,
    createMeta: bg.createMeta,
    jobKey: bg.key,
  };
}

/**
 * @param {Function} hitAxios
 */
export function useClippingJob(hitAxios, { onStarted } = {}) {
  const { jobs, registerClippingJob, subscribe, cancelClippingJob, dismissJob } =
    useBackgroundJobs();

  const [state, setState] = React.useState(INITIAL);
  const [jobKey, setJobKey] = React.useState(null);
  const submittingRef = React.useRef(false);

  React.useEffect(() => {
    if (jobKey) return;
    const clippingJobs = jobs.filter((j) => j.type === "clipping");
    const latest = clippingJobs[clippingJobs.length - 1];
    if (!latest || latest.phase === "dismissed") return;
    setJobKey(latest.key);
    setState(mapBackgroundToState(latest));
  }, [jobs, jobKey]);

  React.useEffect(() => {
    if (!jobKey) return undefined;
    return subscribe(jobKey, (bg) => {
      setState(mapBackgroundToState(bg));
    });
  }, [jobKey, subscribe]);

  const submit = React.useCallback(
    async (payload) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setState({ ...INITIAL, phase: "submitting" });

      try {
        const data = await createClippingJob(hitAxios, payload);
        const requestId = data.requestId;
        const key = registerClippingJob(requestId, data);
        setJobKey(key);
        setState({
          phase: "polling",
          requestId,
          status: null,
          result: null,
          error: null,
          createMeta: data,
          jobKey: key,
        });
        onStarted?.();
      } catch (err) {
        setState((s) => ({
          ...s,
          phase: "failed",
          error: err?.message || "Submit failed.",
        }));
      } finally {
        submittingRef.current = false;
      }
    },
    [hitAxios, onStarted, registerClippingJob],
  );

  const cancel = React.useCallback(async () => {
    const key = jobKey || state.jobKey;
    if (key) {
      await cancelClippingJob(key);
    }
    setJobKey(null);
    setState(INITIAL);
  }, [cancelClippingJob, jobKey, state.jobKey]);

  const reset = React.useCallback(() => {
    const key = jobKey || state.jobKey;
    if (key) dismissJob(key);
    setJobKey(null);
    setState(INITIAL);
  }, [dismissJob, jobKey, state.jobKey]);

  return { ...state, submit, cancel, reset };
}
